/**
 * Subscription Expired Modal Hook
 *
 * Manages state and logic for the subscription expired modal.
 * This modal appears when a Smart plan user's subscription expires.
 *
 * Trigger: planDetails.subscriptionInfo.actionType === 'MANDATORY_SUBSCRIPTION_DECISION'
 *
 * @module @ideoz/core/hooks/facades/shared/useSubscriptionExpiredModal
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useAppSelector, useAppDispatch } from '../../../store/hooks';
import { selectProjects, getUserProjectsWithPagination } from '../../../store/slices/projectSlice';
import apiClient from '../../../api/client';
import { ApiUrls } from '../../../api/ApiUrls';
import { isFreeModeEnabled } from '../../../utils/free-mode';
import { isWebBuild } from '../../../utils/environment';
import type { NavigationFacade, ShowToastFn } from './types';
import type { PlanDetails } from '../../../types/subscription.types';

export interface SubscriptionExpiredModalState {
  /**
   * Whether the modal is open
   */
  isOpen: boolean;

  /**
   * Current plan name (e.g., "Smart")
   */
  planName: string;

  /**
   * Whether downgrade is processing
   */
  isProcessing: boolean;

  /**
   * Handler to close the modal (not used for non-dismissible modal, but kept for consistency)
   */
  onClose: () => void;

  /**
   * Handler for renew subscription action
   */
  onRenewSubscription: () => void;

  /**
   * Handler for proceed to downgrade action
   */
  onProceedToDowngrade: () => void;

  /**
   * Whether user has multiple projects (needed for deciding next flow)
   */
  hasMultipleProjects: boolean;

  /**
   * Project count
   */
  projectCount: number;
}

export interface UseSubscriptionExpiredModalParams {
  /**
   * Navigation facade for redirecting to billing page
   */
  navigation: NavigationFacade;

  /**
   * Plan details from user profile (contains subscriptionInfo)
   */
  planDetails?: PlanDetails | null;

  /**
   * Toast notification function
   */
  showToast?: ShowToastFn;

  /**
   * Callback when user proceeds to downgrade with multiple projects
   * This should open the project archive modal
   */
  onShowProjectArchiveModal?: () => void;

  /**
   * Callback to handle renewal flow - used in plugin to open web billing
   * If not provided, web will use Stripe checkout flow
   */
  onRenew?: () => void;

  /**
   * Smart plan checkout ID (periodicPlans[0].id) for Stripe checkout on web
   * If not provided, will attempt to fetch from available plans API
   */
  smartPlanId?: string;
}

/**
 * Hook to manage subscription expired modal state and actions
 *
 * @param params - Configuration parameters
 * @returns Subscription expired modal state and handlers
 *
 * @example
 * ```tsx
 * const subscriptionExpiredModal = useSubscriptionExpiredModal({
 *   navigation,
 *   planDetails,
 *   showToast,
 *   onShowProjectArchiveModal: () => projectArchiveModal.openModal(),
 *   onRenew: handleStripeRenewal,
 * });
 * ```
 */
export function useSubscriptionExpiredModal({
  navigation,
  planDetails,
  showToast,
  onShowProjectArchiveModal,
  onRenew,
  smartPlanId,
}: UseSubscriptionExpiredModalParams): SubscriptionExpiredModalState {
  const dispatch = useAppDispatch();
  const projects = useAppSelector(selectProjects);
  const isWeb = isWebBuild();
  
  const [isOpen, setIsOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasAutoTriggered, setHasAutoTriggered] = useState(false);
  const [availablePlanId, setAvailablePlanId] = useState<string | null>(null);

  // Get project count
  const projectCount = projects.length;
  const hasMultipleProjects = projectCount > 1;

  // Check if subscription requires mandatory decision (subscription expired)
  const requiresMandatoryDecision = useMemo(() => {
    const subscriptionInfo = planDetails?.subscriptionInfo;
    return subscriptionInfo?.actionType === 'MANDATORY_SUBSCRIPTION_DECISION';
  }, [planDetails]);

  // Get current plan name from planDetails
  const planName = useMemo(() => {
    return planDetails?.name || 'Smart';
  }, [planDetails]);

  // Fetch projects on mount to ensure we have accurate count
  useEffect(() => {
    if (requiresMandatoryDecision && projectCount === 0) {
      dispatch(getUserProjectsWithPagination({ limit: 50, page: 1 }));
    }
  }, [requiresMandatoryDecision, projectCount, dispatch]);

  // Fetch available plans to get Smart plan checkout ID (periodicPlans[0].id) for Stripe checkout (web only)
  useEffect(() => {
    // FREE_MODE_ENABLED bypass — no Stripe checkout while billing is off.
    if (isFreeModeEnabled()) return;
    if (isWeb && requiresMandatoryDecision && !smartPlanId && !availablePlanId) {
      const fetchPlans = async () => {
        try {
          const response = await apiClient.get(ApiUrls.getPlans);
          const plansData = response.data?.data || response.data || [];
          // Find the Smart plan
          const smartPlan = plansData.find((plan: any) => 
            plan.name?.toLowerCase() === 'smart' || 
            plan.planName?.toLowerCase() === 'smart'
          );
          if (smartPlan) {
            // Get the checkout ID from periodicPlans[0].id (first active or first available)
            const periodicPlan = smartPlan.periodicPlans?.find((pp: any) => pp.active) || smartPlan.periodicPlans?.[0];
            const checkoutId = periodicPlan?.id;
            if (checkoutId) {
              setAvailablePlanId(checkoutId);
            }
          }
        } catch (error) {
          console.error('Failed to fetch plans:', error);
        }
      };
      fetchPlans();
    }
  }, [isWeb, requiresMandatoryDecision, smartPlanId, availablePlanId]);

  // Auto-trigger modal when subscription requires mandatory decision
  useEffect(() => {
    if (requiresMandatoryDecision && !isOpen && !hasAutoTriggered) {
      setIsOpen(true);
      setHasAutoTriggered(true);
    }
  }, [requiresMandatoryDecision, isOpen, hasAutoTriggered]);

  // Close modal and reset trigger when condition is no longer met (e.g., fresh data loaded)
  useEffect(() => {
    if (!requiresMandatoryDecision && isOpen) {
      // Fresh data indicates action is no longer required - close the modal
      setIsOpen(false);
      setHasAutoTriggered(false);
    }
  }, [requiresMandatoryDecision, isOpen]);

  // Handler for renew subscription
  const handleRenewSubscription = useCallback(async () => {
    // Plugin: Use provided callback to open web billing
    if (onRenew) {
      onRenew();
      return;
    }

    // Web: Use Stripe checkout flow
    if (isWeb) {
      const planId = smartPlanId || availablePlanId;
      
      if (!planId) {
        // Fallback: Navigate to billing page if no plan ID available
        if (showToast) {
          showToast({
            message: 'Redirecting to billing page...',
            severity: 'info',
          });
        }
        if (navigation.toBilling) {
          navigation.toBilling();
        }
        return;
      }

      setIsProcessing(true);

      try {
        // Build success and cancel URLs for Stripe redirect
        const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
        const successUrl = `${baseUrl}/billing?payment=success`;
        const cancelUrl = `${baseUrl}/billing?payment=cancelled`;

        // Build the API URL
        const checkoutApiUrl = ApiUrls.getCheckoutUrl(planId, successUrl, cancelUrl);

        if (showToast) {
          showToast({
            message: 'Preparing checkout...',
            severity: 'info',
          });
        }

        // Call the backend API to get the Stripe checkout URL
        const response = await apiClient.get(checkoutApiUrl, {
          maxRedirects: 0,
          validateStatus: (status) => status >= 200 && status < 400,
        });

        // Get the Stripe checkout URL from the response
        const stripeCheckoutUrl = response.data?.url;

        if (!stripeCheckoutUrl) {
          throw new Error('No checkout URL received from server');
        }

        // Close the modal before redirect
        setIsOpen(false);

        if (showToast) {
          showToast({
            message: 'Redirecting to Stripe checkout...',
            severity: 'info',
          });
        }

        // Small delay to show the toast, then redirect to Stripe
        await new Promise(resolve => setTimeout(resolve, 300));

        // Redirect to Stripe checkout page
        window.location.href = stripeCheckoutUrl;

      } catch (error: any) {
        console.error('Error initiating renewal:', error);

        // Check if the error response contains a redirect URL
        if (error.response?.headers?.location) {
          const redirectUrl = error.response.headers.location;
          setIsOpen(false);
          if (showToast) {
            showToast({
              message: 'Redirecting to Stripe checkout...',
              severity: 'info',
            });
          }
          await new Promise(resolve => setTimeout(resolve, 300));
          window.location.href = redirectUrl;
          return;
        }

        if (showToast) {
          showToast({
            message: error.message || 'Failed to start renewal. Please try again.',
            severity: 'error',
          });
        }
        setIsProcessing(false);
      }
    } else {
      // Fallback: Navigate to billing page
      if (navigation.toBilling) {
        navigation.toBilling();
      }
    }
  }, [onRenew, isWeb, smartPlanId, availablePlanId, showToast, navigation]);

  // Handler for proceed to downgrade
  const handleProceedToDowngrade = useCallback(async () => {
    setIsProcessing(true);

    try {
      // Call downgrade API
      await apiClient.post(ApiUrls.downgrade);

      // Check project count to determine next step
      if (hasMultipleProjects) {
        // User has multiple projects - show project archive modal
        setIsProcessing(false);
        setIsOpen(false);
        
        if (onShowProjectArchiveModal) {
          onShowProjectArchiveModal();
        }
      } else {
        // User has 0 or 1 project - downgrade is complete
        setIsProcessing(false);
        setIsOpen(false);

        // Show success toast
        if (showToast) {
          showToast({
            message: 'Downgrade to free plan has been done successfully',
            severity: 'success',
          });
        }

        // Redirect to billing page
        if (navigation.toBilling) {
          navigation.toBilling();
        }
      }
    } catch (error) {
      console.error('Failed to process downgrade:', error);
      
      if (showToast) {
        showToast({
          message: error instanceof Error ? error.message : 'Failed to process downgrade',
          severity: 'error',
        });
      }
      
      setIsProcessing(false);
    }
  }, [hasMultipleProjects, navigation, showToast, onShowProjectArchiveModal]);

  const handleClose = useCallback(() => {
    // Modal is non-dismissible, but we keep this for consistency
    // In practice, this won't be called
  }, []);

  return {
    isOpen,
    planName,
    isProcessing,
    onClose: handleClose,
    onRenewSubscription: handleRenewSubscription,
    onProceedToDowngrade: handleProceedToDowngrade,
    hasMultipleProjects,
    projectCount,
  };
}
